import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from '@/lib/toast';
import { normalizeApiError } from '@/lib/apiError';
import { membershipDenial } from '@/lib/membershipErrors';
import { Resource, Policy, Session, AccessLog, AccessRequest, PaginatedResponse, TerminalSessionsResponse, TerminalSession, TerminalActivityLog } from '../types';
import { getCookie, setCookie, removeCookie } from '../utils/cookies';
import { getTenant } from './tenant';
import { BASE_PATH, isLocal, LOCAL_API_URL, apiOrigin, wsOrigin } from './env';

// Re-export shared origin helpers so existing `from '../services/api'` imports keep working.
export { BASE_PATH, isLocal, LOCAL_API_URL };

// WebSocket base. Paths are appended by callers (e.g. `${getSocketUrl()}/api/v1/...`).
// In local dev this points directly at the backend (ws://localhost:8080), else at the
// current host under /zta.
export const getSocketUrl = () => wsOrigin();

// Helpers for raw fetch()/WebSocket calls that bypass the axios instances:
// apiPath prefixes the API origin (direct backend in local dev, /zta in prod);
// authTenantHeaders attaches auth + tenant; wsTenantParam yields "?tenant_id=<t>" for
// WebSocket upgrades (which can't send headers).
export const apiPath = (path: string) => `${apiOrigin()}${path.startsWith('/') ? path : '/' + path}`;
export const authTenantHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = getCookie('auth_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenant = getTenant();
  if (tenant) headers['X-Tenant-Id'] = tenant;
  return headers;
};
export const wsTenantParam = (): string => {
  const tenant = getTenant();
  return tenant ? `tenant_id=${encodeURIComponent(tenant)}` : '';
};

const api = axios.create({
  baseURL: `${apiOrigin()}/api/v1`,
  timeout: 10_000,
});

type LoaderListener = (isLoading: boolean) => void;

let activeGetRequests = 0;
const loaderListeners = new Set<LoaderListener>();

const isGetRequest = (config?: AxiosRequestConfig) => {
  if (!config?.method) {
    return true;
  }
  return config.method.toLowerCase() === 'get';
};

const notifyLoaderListeners = () => {
  const isLoading = activeGetRequests > 0;
  loaderListeners.forEach((listener) => {
    try {
      listener(isLoading);
    } catch (error) {
      console.error('Loader listener error:', error);
    }
  });
};

const incrementLoader = (config?: AxiosRequestConfig) => {
  if (!isGetRequest(config)) {
    return;
  }
  activeGetRequests += 1;
  notifyLoaderListeners();
};

const decrementLoader = (config?: AxiosRequestConfig) => {
  if (!isGetRequest(config)) {
    return;
  }
  activeGetRequests = Math.max(0, activeGetRequests - 1);
  notifyLoaderListeners();
};

const registerLoaderInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use((config) => {
    incrementLoader(config);
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      decrementLoader(response.config);
      return response;
    },
    (error) => {
      decrementLoader(error?.config);
      return Promise.reject(error);
    }
  );
};

export const subscribeToGetLoader = (listener: LoaderListener) => {
  loaderListeners.add(listener);
  listener(activeGetRequests > 0);
  return () => {
    loaderListeners.delete(listener);
  };
};

registerLoaderInterceptors(api);

// Request interceptor to add auth token + tenant header
api.interceptors.request.use((config) => {
  const token = getCookie('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenant = getTenant();
  if (tenant) {
    config.headers['X-Tenant-Id'] = tenant;
  }
  return config;
});

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

export const logoutData = () => {
  removeCookie('auth_token');
  removeCookie('refresh_token');
  removeCookie('user_info');

  if (!window.location.pathname.includes('/login')) {
    window.location.href = `${BASE_PATH}/login`;
  }
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Replace axios's "Request failed with status code N" with the server's own
    // message (or a sentence for timeouts and network failures), so every
    // `${err.message}` toast in the app reads correctly. See lib/apiError.ts.
    normalizeApiError(error);

    // Handle a workspace-membership refusal (lib/membershipErrors.ts): the user
    // authenticated with the IdP but this workspace no longer admits them — not
    // provisioned, suspended, dropped from the directory, wrong organisation, or
    // the workspace itself is suspended. This catches changes mid-session. The
    // login endpoints are excluded — AuthCallback shows its own popup for those,
    // and there is no session to clear yet mid-login.
    const isLoginFlow =
      originalRequest?.url?.includes('/auth/callback') ||
      originalRequest?.url?.includes('/auth/d2auth/pkce/');
    const denial = membershipDenial(error);
    if (!isLoginFlow && denial?.endsSession) {
      toast.error(denial.message);
      logoutData();
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized responses (token expired/invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorCode = error.response?.data?.code;

      // If server explicitly says token expired, logout immediately without refresh attempt
      if (errorCode === 'TOKEN_EXPIRED') {
        console.warn('Token expired, logging out...');
        processQueue(error, null);
        logoutData();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getCookie('refresh_token');

      // Try to refresh token if available
      if (refreshToken) {
        try {
          const response = await authApi.refreshToken(refreshToken);
          const { access_token, refresh_token: newRefreshToken } = response.data;

          // Update stored tokens
          setCookie('auth_token', access_token, 7);
          if (newRefreshToken) {
            setCookie('refresh_token', newRefreshToken, 7);
          }

          // Process queued requests
          processQueue(null, access_token);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout and process queue with error
          processQueue(refreshError, null);
          logoutData();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token available, logout immediately
        isRefreshing = false;
        processQueue(new Error('No refresh token available'), null);
        logoutData();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const resourcesApi = {
  // List resources by role - If role not provided, It will use primary role on backend
  listByRole: (type?: string, limit = 50, offset = 0, role?: string) =>
    api.get<{ resources: Resource[]; total: number; limit: number; offset: number }>('/resources/by-role', {
      params: { type, limit, offset, role },
    }),

  // List all resources for access request purposes (role-filtered, excludes cloud provider types)
  listRequestable: (type?: string, limit = 50, offset = 0) =>
    api.get<{ resources: Resource[]; total: number; limit: number; offset: number }>('/resources/requestable', {
      params: { type, limit, offset },
    }),

  // List all resources (admin, unfiltered) - used by Cloud Provider pages to find their own resources
  listAll: (type?: string, externalId?: string, limit = 50, offset = 0) =>
    api.get<{ resources: Resource[]; total: number; limit: number; offset: number }>('/resources', {
      params: { type, external_id: externalId, limit, offset },
    }),

  get: (id: string) =>
    api.get<Resource>(`/resources/${id}`),

  create: (resource: Omit<Resource, 'id' | 'created_at' | 'updated_at' | 'last_seen'>) =>
    api.post<Resource>('/resources', resource),

  update: (id: string, resource: Partial<Resource>) =>
    api.put<Resource>(`/resources/${id}`, resource),

  delete: (id: string) =>
    api.delete(`/resources/${id}`),

  // Get access status for a resource (used for Connect button color)
  getAccessStatus: (id: string) =>
    api.get<{
      has_access: boolean;
      status: 'active' | 'expired' | 'revoked' | 'none';
      expires_at: string | null;
      resource_type: string;
      resource_name: string;
    }>(`/resources/${id}/access-status`),
};

// Kubernetes API
export const k8sApi = {
  // List the namespaces of a cluster (via its agent), for scoping access requests.
  listNamespaces: (cluster: string) =>
    api.get<{ namespaces: string[] }>(`/k8s/${encodeURIComponent(cluster)}/namespaces`),
};

// Proxy Resources API
export const proxyResourcesApi = {
  listByRole: (limit = 50, offset = 0, role?: string) =>
    api.get<{ resources: any[]; count: number; total: number; limit: number; offset: number }>('/resources/proxy/by-role', { params: { limit, offset, role } }),

  get: (id: string) =>
    api.get<any>(`/resources/proxy/${id}`),

  create: (resource: any) =>
    api.post<any>('/resources/proxy', resource),

  update: (id: string, resource: any) =>
    api.put<any>(`/resources/proxy/${id}`, resource),

  delete: (id: string) =>
    api.delete(`/resources/proxy/${id}`),

  import: (config: any) =>
    api.post<{ imported: any[]; count: number; errors: string[] }>('/resources/proxy/import', { config }),

  exportConfig: () =>
    api.get('/mpg/routing-config?format=yaml', {
      headers: { Accept: 'application/x-yaml' }
    }),
};

export const policiesApi = {
  list: (activeOnly = false, limit = 50, offset = 0) =>
    api.get<{ policies: Policy[]; total: number; limit: number; offset: number }>('/policies', {
      params: { active: activeOnly, limit, offset },
    }),

  get: (id: string) =>
    api.get<Policy>(`/policies/${id}`),

  create: (policy: Omit<Policy, 'id' | 'created_at'>) =>
    api.post<Policy>('/policies', policy),

  update: (id: string, policy: Partial<Policy>) =>
    api.put<Policy>(`/policies/${id}`, policy),

  delete: (id: string) =>
    api.delete(`/policies/${id}`),
};

export const sessionsApi = {
  list: (userId?: string, limit = 50, offset = 0) =>
    api.get<{ sessions: Session[]; total: number; limit: number; offset: number }>('/sessions', {
      params: { user_id: userId, limit, offset },
    }),

  get: (id: string) =>
    api.get<Session>(`/sessions/${id}`),

  terminate: (id: string) =>
    api.delete(`/sessions/${id}`),

  getRecording: (id: string) =>
    api.get<any>(`/sessions/${id}/recording`, { timeout: 60000 }),

  getRecordingStream: (id: string) =>
    api.get<ArrayBuffer>(`/sessions/${id}/recording-stream`, {
      timeout: 60000,
      responseType: 'arraybuffer',
    }),

  getRecordingVideo: (id: string) =>
    api.get<ArrayBuffer>(`/sessions/${id}/recording-video`, {
      timeout: 120000,
      responseType: 'arraybuffer',
    }),

  uploadRecordingVideo: (id: string, videoBlob: Blob) =>
    api.post(`/sessions/${id}/upload-recording`, videoBlob, {
      timeout: 120000,
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
};

export const accessLogsApi = {
  list: (userId?: string, resourceId?: string, result?: string, limit = 50, offset = 0) =>
    api.get<{ access_logs: AccessLog[]; total: number; limit: number; offset: number }>('/access-logs', {
      params: { user_id: userId, resource_id: resourceId, result, limit, offset },
    }),

  get: (id: string) =>
    api.get<AccessLog>(`/access-logs/${id}`),
};

// Mock API for access requests (to be implemented in backend)
export const accessRequestsApi = {
  list: (status?: string, limit = 50, offset = 0) =>
    api.get<PaginatedResponse<AccessRequest>>('/access-requests', {
      params: { status, limit, offset },
    }),

  getMyApprovals: () =>
    api.get<PaginatedResponse<AccessRequest>>('/access-requests/my-approvals'),

  create: (request: Omit<AccessRequest, 'id' | 'requested_by' | 'resource_name' | 'requested_at' | 'status'>) =>
    api.post<AccessRequest>('/access-requests', request),

  approve: (id: string, awsRoleArn?: string, azureRoleName?: string, hetznerPermissionName?: string) => {
    const body: any = {};
    if (awsRoleArn) body.aws_role_arn = awsRoleArn;
    if (azureRoleName) body.azure_role_name = azureRoleName;
    if (hetznerPermissionName) body.hetzner_permission_name = hetznerPermissionName;
    return api.post(`/access-requests/${id}/approve`, body);
  },

  deny: (id: string, reason: string) =>
    api.post(`/access-requests/${id}/deny`, { denial_reason: reason }),

  revoke: (id: string) =>
    api.post(`/access-requests/${id}/revoke`, {}),

  updateDuration: (id: string, durationSeconds: number) =>
    api.patch<{ message: string; status: string; duration_seconds: number }>(`/access-requests/${id}/duration`, { duration_seconds: durationSeconds }),
};

// Requestable Roles API
export const rolesApi = {
  listRequestable: () =>
    api.get<{ roles: { id: string; key: string; name: string; is_admin: boolean }[] }>('/roles/requestable'),
};

// SSH Resources API - Direct Agent Hub endpoints (not under /api/v1/), served
// under the /zta base path and proxied to the agent hub by the frontend.
const agentApi = axios.create({
  baseURL: `${apiOrigin()}/`,
  timeout: 10_000,
});

registerLoaderInterceptors(agentApi);

// Add auth token + tenant header to agent API requests (some endpoints are public)
agentApi.interceptors.request.use((config) => {
  const token = getCookie('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenant = getTenant();
  if (tenant) {
    config.headers['X-Tenant-Id'] = tenant;
  }
  return config;
});

// Handle 401/403 gracefully for agent endpoints since some might be public
agentApi.interceptors.response.use(
  (response) => response,
  (error) => {
    normalizeApiError(error);
    const errorCode = error.response?.data?.code;

    // If server explicitly says token expired, logout immediately
    if (error.response?.status === 401 && errorCode === 'TOKEN_EXPIRED') {
      console.warn('Token expired on agent endpoint, logging out...');
      logoutData();
      return Promise.reject(error);
    }

    // For other agent endpoint auth errors, just log warning since they might be public
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('Agent endpoint auth failed, might be public endpoint:', error.config.url);
    }
    return Promise.reject(error);
  }
);

export const sshApi = {
  // Get SSH agents status from Agent Hub
  getAgents: () =>
    agentApi.get<{ agents: any[]; total: number; memory_count: number; redis_count: number; storage_info: string }>('/agent/status'),

  // Create SSH session
  createSession: (serverID: string, resourceID: string, user?: string, cols?: number, rows?: number) =>
    agentApi.post<{ status: string; session_id: string }>(`/agent/ssh/${serverID}/resource/${resourceID}`, {
      user: user || 'root',
      cols: cols || 120,
      rows: rows || 40,
      session_id: undefined // Let server generate
    }),

  // Terminate SSH session
  terminateSession: (sessionID: string) =>
    agentApi.delete(`/agent/ssh/${sessionID}`),

  // Get Agent Hub health
  getHealth: () =>
    agentApi.get<{ status: string; agents: number; sessions: number; redis_status: string; redis_url: string; storage: string; timestamp: string }>('/agent/health'),
};

// Responses of the single-fixed-callback login flow (see pkg/api/pkce.go).
export interface PkceStartResponse {
  state: string;
  code_challenge: string;
  code_challenge_method: string;
  redirect_uri: string;
  // Built server-side, so the issuer and client id never need to be baked into
  // this bundle.
  auth_url: string;
}

export interface PkceCompleteResponse {
  access_token: string;
  expires_in: number;
  tenant_slug: string;
  session_claim: string;
  // Absolute URL of the tenant's own origin, with the one-time claim attached.
  redirect_to: string;
}

export interface PkceClaimResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  tenant_slug: string;
  user_info: any;
}

export const authApi = {
  // OIDC Authentication Flow
  getAuthUrl: () =>
    api.get<{ auth_url: string; state: string }>('/auth/login'),

  handleCallback: (code: string, state: string) =>
    api.post<{ access_token: string; id_token: string; refresh_token: string; user_info: any }>('/auth/callback', { code, state }),

  // --- Single fixed d2auth callback, server-side PKCE ---------------------
  // One redirect URI is registered with d2auth for the whole product; the tenant
  // is remembered on the server against `state` instead of being encoded in the
  // callback URL, so onboarding a tenant no longer needs an IdP change.
  // These three are public (no session exists yet at any of these steps).

  // Step 1, from the tenant's own origin: get a state + the authorize URL.
  pkceStart: (tenant: string) =>
    api.post<PkceStartResponse>('/auth/d2auth/pkce/start', { tenant }),

  // Step 2, from the shared callback host: redeem code + state. The server
  // recovers the tenant, finishes the exchange, checks workspace access, and
  // returns where to send the browser next.
  pkceComplete: (code: string, state: string) =>
    api.post<PkceCompleteResponse>('/auth/d2auth/pkce/complete', { code, state }),

  // Step 3, back on the tenant's own origin: trade the one-time claim for the
  // real tokens, which are stored as cookies here — on the origin that uses them.
  pkceClaim: (code: string) =>
    api.post<PkceClaimResponse>('/auth/d2auth/pkce/claim', { code }),

  refreshToken: (refreshToken: string) =>
    api.post<{ access_token: string; id_token: string; refresh_token: string }>('/auth/refresh', { refresh_token: refreshToken }),

  logout: async () => {
    const refreshToken = getCookie('refresh_token');
    // Call backend to invalidate d2auth session; regardless of result, clear local state
    try {
      try {
        return await api.post('/auth/logout', { refresh_token: refreshToken });
      } catch (err) {
        console.warn('Logout request failed, clearing client state anyway:', err);
      }
    } finally {
      logoutData();
    }
  },

  getUserInfo: () =>
    api.get<any>('/auth/userinfo'),
};


// Terminal Activity Logs API
export const terminalLogsApi = {
  // List terminal sessions grouped with logs (for UI)
  listSessionsGrouped: (params?: {
    user_id?: string;
    resource_name?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<TerminalSessionsResponse>('/terminal-sessions-grouped', { params }),

  // List terminal sessions
  listSessions: (params?: {
    user_id?: string;
    resource_name?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{ sessions: TerminalSession[]; total: number; limit: number; offset: number }>('/terminal-sessions', { params }),

  // Get single terminal session
  getSession: (id: string) =>
    api.get<TerminalSession>(`/terminal-sessions/${id}`),

  // Get session activity logs
  getSessionLogs: (id: string, params?: {
    event_type?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{ logs: TerminalActivityLog[]; total: number; limit: number; offset: number }>(`/terminal-sessions/${id}/logs`, { params }),

  // Update session footage URL
  updateFootage: (id: string, footageUrl: string) =>
    api.put(`/terminal-sessions/${id}/footage`, { footage_url: footageUrl }),

};

// Files API - handle secure file downloads via backend proxy
export const filesApi = {
  // Download a file by blob name (served via backend)
  downloadByBlobName: (blobName: string) =>
    api.get(`/files/${encodeURIComponent(blobName)}`, { responseType: 'blob' }),

  // Upload a file (multipart/form-data) -> returns { url, filename, blobName, message }
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ url: string; filename: string; blobName: string; message: string }>(
      '/files/upload',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// Certificates API
export const certificatesApi = {
  list: (filters?: Record<string, string>) =>
    api.get('/certificates', { params: filters }),

  get: (id: string) =>
    api.get(`/certificates/${id}`),

  issue: (data: { type: string; subject?: { common_name: string }; validity_hours?: number; principals?: string[] }) =>
    api.post('/certificates/issue', data),

  issueSSH: (data: { principals?: string[]; validity_hours?: number; extensions?: Record<string, string> }) =>
    api.post('/ssh/certificates', data),

  issueTLS: (data: { subject?: { common_name: string; organization?: string }; dns_names: string[]; validity_hours?: number }) =>
    api.post('/tls/certificates', data),

  revoke: (id: string, reason?: string) =>
    api.delete(`/certificates/${id}/revoke`, { data: { reason } }),

  getRoot: (type: string) =>
    api.get(`/certificates/roots/${type}`),

  getSSHCA: () =>
    api.get('/ssh/ca-key'),

  getTLSCA: () =>
    api.get('/tls/ca-cert'),
};

// Settings API (admin-only)
export const settingsApi = {
  getGeneral: () =>
    api.get('/settings'),

  updateGeneral: (data: Record<string, unknown>) =>
    api.put('/settings', data),

  getAuthConfig: () =>
    api.get('/settings/auth'),

  getAgentTokens: () =>
    api.get('/settings/agent-tokens'),

  rotateAgentToken: (category: string) =>
    api.post(`/settings/agent-tokens/${category}/rotate`),

  getAuditConfig: () =>
    api.get('/settings/audit'),

  updateAuditConfig: (data: Record<string, unknown>) =>
    api.put('/settings/audit', data),

  getDomains: () =>
    api.get<{ domains: string[] }>('/settings/domains'),
};

// AWS Resources API
export const awsApi = {
  // AWS Accounts
  listAccounts: () =>
    api.get<{ accounts: any[]; total: number }>('/aws/accounts'),

  createAccount: (data: {
    account_id: string;
    account_name: string;
    regions: string[];
    assume_role_arn: string;
    external_id?: string;
  }) =>
    api.post<any>('/aws/accounts', data),

  updateAccount: (id: string, data: any) =>
    api.put<any>(`/aws/accounts/${id}`, data),

  deleteAccount: (id: string) =>
    api.delete(`/aws/accounts/${id}`),

  // AWS Resources (discovered)
  listResources: (type?: string, accountId?: string, region?: string, limit = 50, offset = 0) =>
    api.get<{ resources: any[]; total: number; limit: number; offset: number }>('/aws/resources', {
      params: { type, account_id: accountId, region, limit, offset },
    }),

  // Trigger discovery
  triggerDiscovery: () =>
    api.post<{ status: string; total_discovered: number; accounts_scanned: number }>('/aws/discover'),

  // Generate Console URL
  generateConsoleUrl: (data: {
    aws_account_id: string;
    resource_type?: string;
    resource_id?: string;
    destination?: string;
    aws_role_arn?: string;
  }) =>
    api.post<{
      console_url: string;
      expires_at: string;
      role_assumed: string;
      account_id: string;
      account_name: string;
      audit_id: string;
    }>('/aws/console-url', data),

  // Approved Roles for Account
  getApprovedRoles: (accountId: string) =>
    api.get<{
      roles: Array<{
        aws_role_arn: string;
        role_name: string;
        expires_at: string;
        request_id: string;
      }>;
    }>(`/aws/accounts/${accountId}/approved-roles`),

  // Role Mappings
  listRoleMappings: (iapRoleId?: string, awsAccountId?: string) =>
    api.get<{ mappings: any[]; total: number }>('/aws/role-mappings', {
      params: { iap_role_id: iapRoleId, aws_account_id: awsAccountId },
    }),

  createRoleMapping: (data: {
    iap_role_id?: string;
    aws_account_id: string;
    aws_role_arn: string;
    max_session_duration?: number;
  }) =>
    api.post<any>('/aws/role-mappings', data),

  deleteRoleMapping: (id: string) =>
    api.delete(`/aws/role-mappings/${id}`),

  // Console Sessions (audit)
  listConsoleSessions: (limit = 50, offset = 0) =>
    api.get<{ sessions: any[]; total: number }>('/aws/console-sessions', {
      params: { limit, offset },
    }),

  // Service Catalog
  listServiceCatalog: () =>
    api.get<{ services: any[] }>('/aws/services/catalog'),

  listServices: (accountId?: string) =>
    api.get<{ services: any[]; account_id?: string }>('/aws/services', {
      params: { account_id: accountId },
    }),

  enableService: (awsAccountId: string, serviceType: string) =>
    api.post<any>('/aws/services', { aws_account_id: awsAccountId, service_type: serviceType }),

  disableService: (accountId: string, serviceType: string) =>
    api.delete(`/aws/services/${accountId}/${serviceType}`),
};

// ===== Azure API =====
export const azureApi = {
  // Subscriptions
  listSubscriptions: () =>
    api.get<{ subscriptions: any[]; total: number }>('/azure/subscriptions'),

  createSubscription: (data: {
    subscription_id: string;
    subscription_name: string;
    tenant_id: string;
    client_id: string;
    client_secret: string;
  }) =>
    api.post<any>('/azure/subscriptions', data),

  deleteSubscription: (id: string) =>
    api.delete(`/azure/subscriptions/${id}`),

  // Resources
  listResources: (type?: string, subscriptionId?: string, region?: string, limit = 50, offset = 0) =>
    api.get<{ resources: any[]; total: number }>('/azure/resources', {
      params: { type, subscription_id: subscriptionId, region, limit, offset },
    }),

  // Discovery
  triggerDiscovery: () =>
    api.post<{ total_discovered: number; subscriptions_scanned: number; errors: string[] }>('/azure/discover', {}),

  // Service Catalog
  listServiceCatalog: () =>
    api.get<{ services: any[] }>('/azure/services/catalog'),

  listServices: (subscriptionId?: string) =>
    api.get<{ services: any[]; subscription_id?: string }>('/azure/services', {
      params: { subscription_id: subscriptionId },
    }),

  enableService: (subscriptionId: string, serviceType: string) =>
    api.post<any>('/azure/services', { azure_subscription_id: subscriptionId, service_type: serviceType }),

  disableService: (subscriptionId: string, serviceType: string) =>
    api.delete(`/azure/services/${subscriptionId}/${serviceType}`),

  // Role Mappings
  listRoleMappings: (subscriptionId?: string) =>
    api.get<{ mappings: any[]; total: number }>('/azure/role-mappings', {
      params: subscriptionId ? { azure_subscription_id: subscriptionId } : {},
    }),

  createRoleMapping: (data: { azure_subscription_id: string; azure_role_name: string; scope?: string }) =>
    api.post<any>('/azure/role-mappings', data),

  deleteRoleMapping: (id: string) =>
    api.delete(`/azure/role-mappings/${id}`),
};

// ===== Hetzner API =====
export const hetznerApi = {
  // Projects
  listProjects: () =>
    api.get<{ projects: any[]; total: number }>('/hetzner/projects'),

  createProject: (data: { project_name: string; api_token: string }) =>
    api.post<any>('/hetzner/projects', data),

  deleteProject: (id: string) =>
    api.delete(`/hetzner/projects/${id}`),

  // Resources
  listResources: (type?: string, projectId?: string, limit = 50, offset = 0) =>
    api.get<{ resources: any[]; total: number }>('/hetzner/resources', {
      params: { type, project_id: projectId, limit, offset },
    }),

  // Discovery
  triggerDiscovery: () =>
    api.post<{ total_discovered: number; projects_scanned: number; errors: string[] }>('/hetzner/discover', {}),

  // Service Catalog
  listServiceCatalog: () =>
    api.get<{ services: any[] }>('/hetzner/services/catalog'),

  listServices: (projectId?: string) =>
    api.get<{ services: any[]; project_id?: string }>('/hetzner/services', {
      params: { project_id: projectId },
    }),

  enableService: (projectId: string, serviceType: string) =>
    api.post<any>('/hetzner/services', { hetzner_project_id: projectId, service_type: serviceType }),

  disableService: (projectId: string, serviceType: string) =>
    api.delete(`/hetzner/services/${projectId}/${serviceType}`),

  // Role Mappings
  listRoleMappings: (projectId?: string) =>
    api.get<{ mappings: any[]; total: number }>('/hetzner/role-mappings', {
      params: projectId ? { hetzner_project_id: projectId } : {},
    }),

  createRoleMapping: (data: { hetzner_project_id: string; permission_name: string; api_token_scope?: string }) =>
    api.post<any>('/hetzner/role-mappings', data),

  deleteRoleMapping: (id: string) =>
    api.delete(`/hetzner/role-mappings/${id}`),
};

export const gcpApi = {
  // Projects
  listProjects: () =>
    api.get<{ projects: any[] }>('/gcp/projects'),

  createProject: (data: {
    project_id: string;
    project_name: string;
    service_account_email: string;
    credentials_json: string;
  }) => api.post<any>('/gcp/projects', data),

  // Resources
  listResources: (type?: string, projectId?: string, limit: number = 50, offset: number = 0) =>
    api.get<{ resources: any[]; total: number }>('/gcp/resources', {
      params: { type, project_id: projectId, limit, offset },
    }),

  // Console Access
  generateConsoleUrl: (data: {
    resource_type: string;
    resource_id: string;
    project_id: string;
    role_name: string;
    destination_url: string;
  }) => api.post<{ console_url: string; access_token: string; expires_at: string }>('/gcp/console-url', data),

  // Role Mappings
  listRoleMappings: (projectId?: string) =>
    api.get<{ mappings: any[] }>('/gcp/role-mappings', {
      params: projectId ? { project_id: projectId } : {},
    }),

  createRoleMapping: (data: { iap_role_id?: string; gcp_project_id: string; gcp_role_name: string; max_session_duration?: number }) =>
    api.post<any>('/gcp/role-mappings', data),
};

export default api;
